"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import Tier1Section from "./components/Tier1Section";
import Tier2Section from "./components/Tier2Section";
import CtaBanner from "./components/CtaBanner";
import Tier3Section from "./components/Tier3Section";
import ResponseTimeSection from "./components/ResponseTimeSection";
import OurScopeSection from "./components/OurScopeSection";
import AreasNotServedSection from "./components/AreasNotServedSection";
import BottomCtaSection from "./components/BottomCtaSection";
import { injectMetaTags } from "@/lib/seoHelpers";

const allAreas = [
  { "@type": "City", name: "Brickell" },
  { "@type": "City", name: "Aventura" },
  { "@type": "City", name: "Miami Beach" },
  { "@type": "City", name: "Coral Gables" },
  { "@type": "City", name: "Doral" },
  { "@type": "City", name: "Coconut Grove" },
  { "@type": "City", name: "Kendall" },
  { "@type": "City", name: "Pinecrest" },
  { "@type": "City", name: "Bay Harbor Islands" },
  { "@type": "City", name: "Key Biscayne" },
  { "@type": "City", name: "South Miami" },
  { "@type": "City", name: "North Miami Beach" },
  { "@type": "City", name: "Sunny Isles Beach" },
  { "@type": "City", name: "Hialeah" },
  { "@type": "City", name: "Surfside" },
  { "@type": "City", name: "Bal Harbour" },
  { "@type": "City", name: "Cutler Bay" },
  { "@type": "City", name: "Palmetto Bay" },
  { "@type": "City", name: "Homestead" },
  { "@type": "City", name: "Miami Shores" },
];

export default function ServiceAreasPage() {
  const { currentLang, t } = useLanguage();
  useHreflang();

  useEffect(() => {
    document.title = t("serviceAreas.seoTitle");

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", t("serviceAreas.seoDesc"));
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = t("serviceAreas.seoDesc");
      document.head.appendChild(meta);
    }

    const cleanupSeo = injectMetaTags({
      title: t("serviceAreas.seoTitle"),
      description: t("serviceAreas.seoDesc"),
      url: "https://impact-window-repair-miami.com/service-areas/",
      image: "https://impact-window-repair-miami.com/og/og-service-areas.jpg",
      imageAlt: t("serviceAreas.heroImageAlt"),
      breadcrumbs: [
        { name: t("serviceAreas.breadcrumbHome"), item: "https://impact-window-repair-miami.com/" },
        { name: t("serviceAreas.breadcrumbName"), item: "https://impact-window-repair-miami.com/service-areas/" }
      ],
      type: "website",
    });

    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: t("global.companyName"),
      telephone: "+17863774452",
      email: "contact@impact-window-repair-miami.com",
      url: "https://impact-window-repair-miami.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "848 Brickell Ave, PH-5",
        addressLocality: "Miami",
        addressRegion: "FL",
        postalCode: "33131",
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        "latitude": 25.7617,
        "longitude": -80.1918
      },
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "07:00",
        "closes": "21:00"
      }],
      priceRange: "$$",
      paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
      currenciesAccepted: "USD",
      areaServed: [
        {"@type": "City", "name": "Miami"},
        {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
        "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
      ],
      knowsLanguage: ["en", "es"],
      knowsAbout: [
        t("global.schemaSvcHardware"),
        t("global.schemaSvcSliding"),
        t("global.schemaSvcGlass"),
        t("global.schemaSvcWeather"),
        t("global.schemaSvcMaint"),
        t("global.schemaSvcBoardup")
      ],
      slogan: t("global.slogan"),
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      provider: {
        "@type": "LocalBusiness",
        name: t("global.companyName"),
      },
      areaServed: allAreas,
      serviceType: [
        t("global.schemaSvcSliding"),
        t("global.schemaSvcGlass"),
        t("global.schemaSvcHardware"),
        t("global.schemaSvcWeather"),
        t("global.schemaSvcMaint"),
        t("global.schemaSvcBoardup"),
      ],
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
    };

    const script1 = document.createElement("script");
    script1.type = "application/ld+json";
    script1.text = JSON.stringify(localBusinessSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement("script");
    script2.type = "application/ld+json";
    script2.text = JSON.stringify(serviceSchema);
    document.head.appendChild(script2);

    return () => {
      cleanupSeo();
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, [currentLang, t]);

  return (
    <main className="w-full">
      <HeaderStrip />
      <HeroSection />
      <Tier1Section />
      <Tier2Section />
      <CtaBanner />
      <Tier3Section />
      <ResponseTimeSection />
      <OurScopeSection />
      <AreasNotServedSection />
      <BottomCtaSection />
    </main>
  );
}